import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, BookOpen, ClipboardCheck, Sparkles, LogOut, GraduationCap, Layers, Video, Target, FileText, Award, Shield } from "lucide-react";
import LearningTipPopup from "@/components/LearningTipPopup";

const items = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/modules", label: "Curriculum", icon: BookOpen, testid: "nav-modules" },
  { to: "/study-kit", label: "Study Kit", icon: FileText, testid: "nav-studykit" },
  { to: "/flashcards", label: "Flashcards", icon: Layers, testid: "nav-flashcards" },
  { to: "/videos", label: "Video Library", icon: Video, testid: "nav-videos" },
  { to: "/practice-exam", label: "Practice Exam", icon: ClipboardCheck, testid: "nav-practice" },
  { to: "/missed-drill", label: "Missed Drill", icon: Target, testid: "nav-missed" },
  { to: "/certificate", label: "Certificate", icon: Award, testid: "nav-certificate" },
  { to: "/tutor", label: "AI Tutor", icon: Sparkles, testid: "nav-tutor" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen flex bg-[var(--dw-parchment)]">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 shrink-0 bg-[var(--dw-navy)] text-[var(--dw-parchment)] border-r border-white/5">
        <div className="px-6 py-6 border-b border-white/8">
          <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="brand-link-sidebar">
            <GraduationCap className="w-6 h-6 text-[var(--dw-gold)]" strokeWidth={1.5} />
            <div>
              <div className="font-serif text-lg leading-tight">DW Training</div>
              <div className="text-[10px] tracking-[0.22em] text-[var(--dw-gold)] uppercase">Academy</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 py-5 px-3 space-y-1">
          {items.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
                  isActive
                    ? "bg-[var(--dw-gold)]/12 text-[var(--dw-gold)] border-l-2 border-[var(--dw-gold)]"
                    : "text-white/70 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={1.5} />
              <span>{label}</span>
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors mt-4 ${
                  isActive
                    ? "bg-[var(--dw-gold)]/15 text-[var(--dw-gold)] border-l-2 border-[var(--dw-gold)]"
                    : "text-[var(--dw-gold)]/80 hover:text-[var(--dw-gold)] hover:bg-[var(--dw-gold)]/8 border-l-2 border-transparent"
                }`
              }
            >
              <Shield className="w-4 h-4" strokeWidth={1.5} />
              <span>Admin Console</span>
            </NavLink>
          )}
        </nav>

        <div className="px-4 py-4 border-t border-white/8">
          <div className="text-xs text-white/50 mb-2">Signed in as</div>
          <div className="text-sm font-medium" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-xs text-white/60 mb-3 truncate" data-testid="sidebar-user-email">{user?.email}</div>
          <button
            onClick={async () => { await logout(); nav("/"); }}
            data-testid="logout-btn"
            className="w-full flex items-center justify-center gap-2 text-xs text-white/70 hover:text-[var(--dw-gold)] py-2 border border-white/10 rounded-sm transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="lg:hidden fixed top-0 inset-x-0 bg-[var(--dw-navy)] text-white z-40 border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/dashboard" className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-[var(--dw-gold)]" strokeWidth={1.5} />
            <span className="font-serif">DW Training</span>
          </Link>
          <button onClick={async () => { await logout(); nav("/"); }} className="text-xs text-white/70">
            Sign out
          </button>
        </div>
        <div className="flex gap-0.5 px-1 pb-1 overflow-x-auto">
          {items.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-1.5 whitespace-nowrap px-3 py-2 text-xs rounded-sm ${
                  isActive ? "bg-[var(--dw-gold)]/15 text-[var(--dw-gold)]" : "text-white/70"
                }`
              }
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {label}
            </NavLink>
          ))}
        </div>
      </div>

      <main className="flex-1 lg:pl-0 pt-24 lg:pt-0 overflow-x-hidden">
        {children}
      </main>
      <LearningTipPopup />
    </div>
  );
}
